import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DEFAULT_MARKETPLACE_RANGE,
  formatPercent,
  trackMarketplaceAnalyticsEvent,
} from "../../../../constants/automapMarketplaceAnalyticsConstants";
import {
  fetchMarketplaceHealthSummary,
  exportMarketplaceAnalyticsCsv,
} from "../../../../services/automapMarketplaceAnalyticsApi";
import {
  AnalyticsBreakdownTable,
  AnalyticsSummaryGrid,
  FunnelStepsTable,
} from "../AdminAnalyticsShared";
import {
  MarketplaceAnalyticsShell,
  RecommendedActionsPanel,
} from "./AdminMarketplaceAnalyticsShared";

export default function AdminMarketplaceAnalyticsPage() {
  const [range, setRange] = useState(DEFAULT_MARKETPLACE_RANGE);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchMarketplaceHealthSummary(range)
      .then(setPayload)
      .catch((err) => setError(err.message || "Error al cargar analytics."))
      .finally(() => setLoading(false));
  }, [range]);

  const snapshot = payload?.snapshot;

  const handleExport = async () => {
    setExporting(true);
    try {
      trackMarketplaceAnalyticsEvent("marketplace_analytics_export_clicked", { range });
      const { blob, filename } = await exportMarketplaceAnalyticsCsv(range);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Error al exportar.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <MarketplaceAnalyticsShell
      active="marketplace"
      title="Salud del marketplace"
      description="Supply, demanda, embudo, ciudades, SEO, campañas e inventario."
      range={range}
      onRangeChange={setRange}
      viewEvent="marketplace_analytics_viewed"
      toolbarExtra={
        <button type="button" className="dealer-portal-btn" disabled={exporting} onClick={handleExport}>
          {exporting ? "Exportando…" : "Exportar CSV"}
        </button>
      }
    >
      {loading ? <p className="dealer-state-box">Cargando…</p> : null}
      {error ? <p className="dealer-state-box">{error}</p> : null}
      {!loading && !error && snapshot ? (
        <>
          {payload.planOverlay ? (
            <section className="dealer-state-box" style={{ marginBottom: "1rem" }}>
              <h3>Dealers por plan</h3>
              <div className="dashboard-lead-stats">
                {(payload.planOverlay.dealersByPlan || []).map((row) => (
                  <span key={row.planKey}>{row.planKey}: {row.count}</span>
                ))}
                <span>Conversiones trial→paid: {payload.planOverlay.trialToPaidConversions ?? 0}</span>
                <span>Candidatos upgrade: {payload.planOverlay.upgradeCandidates ?? 0}</span>
              </div>
              <Link to="/admin/plans">Abrir planes →</Link>
            </section>
          ) : null}
          {payload.partnershipOverlay ? (
            <section className="dealer-state-box" style={{ marginBottom: "1rem" }}>
              <h3>Partner leads</h3>
              <div className="dashboard-lead-stats">
                <span>Total: {payload.partnershipOverlay.partnerLeadCount ?? 0}</span>
                <span>CTA clicks: {payload.partnershipOverlay.serviceCtaClickThrough ?? 0}</span>
                <span>Conversión: {payload.partnershipOverlay.partnerConversionPlaceholder ?? "—"}</span>
              </div>
              <Link to="/admin/analytics/partners">Analytics aliados →</Link>
            </section>
          ) : null}
          {payload.trialOverlay ? (
            <section className="dealer-state-box" style={{ marginBottom: "1rem" }}>
              <h3>Dealers en trial</h3>
              <div className="dashboard-lead-stats">
                <span>Trials: {payload.trialOverlay.trialDealers ?? 0}</span>
                <span>Leads trial: {payload.trialOverlay.trialLeads ?? 0}</span>
                <span>Listos convertir: {payload.trialOverlay.readyToConvert ?? 0}</span>
                <span>Atención: {payload.trialOverlay.needsAttention ?? 0}</span>
              </div>
              <Link to="/admin/dealers/trials">Abrir trial dashboard →</Link>
            </section>
          ) : null}
          {payload.launchOverlay ? (
            <section className="dealer-state-box" style={{ marginBottom: "1rem" }}>
              <h3>Periodo de lanzamiento</h3>
              <p className="muted">
                Estado: {payload.launchOverlay.launchStatus || "—"}
                {payload.launchOverlay.startDate ? ` · ${payload.launchOverlay.startDate.slice(0, 10)}` : ""}
              </p>
              <div className="dashboard-lead-stats">
                <span>Leads launch: {payload.launchOverlay.leadsDuringLaunch ?? 0}</span>
                <span>Campañas: {payload.launchOverlay.campaignContribution ?? 0}</span>
                <span>SEO: {payload.launchOverlay.seoContribution ?? 0}</span>
              </div>
              <Link to="/admin/campaigns/launch/daily-monitor">Abrir daily monitor →</Link>
            </section>
          ) : null}
          <AnalyticsSummaryGrid
            items={[
              { label: "Listings activos", value: snapshot.totalActiveListings },
              { label: "Dealers activos", value: snapshot.activeDealers },
              { label: "Verificados", value: snapshot.verifiedDealers },
              { label: "Sesiones compradores", value: snapshot.totalBuyerSessions },
              { label: "Búsquedas", value: snapshot.totalSearches },
              { label: "Vistas detalle", value: snapshot.totalVehicleDetailViews },
              { label: "Clics WhatsApp", value: snapshot.totalWhatsappClicks },
              { label: "Leads", value: snapshot.totalLeads },
              { label: "Conv. lead", value: formatPercent(snapshot.leadConversionRate) },
              { label: "Frescura inventario", value: formatPercent(snapshot.inventoryFreshnessPercent) },
              { label: "Calidad promedio", value: snapshot.averageListingQualityScore ?? "—" },
              { label: "Casos confianza", value: snapshot.trustSafetyOpenCases },
            ]}
          />
          <FunnelStepsTable steps={payload.funnel} />
          <div className="admin-analytics-grid">
            <AnalyticsBreakdownTable title="Top ciudades (oferta)" rows={payload.supply?.topCities} keyLabel="Ciudad" />
            <AnalyticsBreakdownTable title="Top fuentes" rows={payload.topSources} keyLabel="Fuente" />
            <AnalyticsBreakdownTable title="Top campañas" rows={payload.topCampaigns} keyLabel="Campaña" />
            <AnalyticsBreakdownTable title="Top dealers (leads)" rows={payload.topDealers} keyLabel="Dealer" />
          </div>
          <section className="dealer-state-box">
            <h3>Resumen por área</h3>
            <div className="admin-analytics-grid">
              <div>
                <h4>Oferta</h4>
                <p>Activos: {payload.supply?.activeListings} · Stale: {payload.supply?.stale}</p>
                <Link to="/admin/analytics/supply">Ver oferta →</Link>
              </div>
              <div>
                <h4>Demanda</h4>
                <p>Leads: {payload.demand?.leads} · Calificados: {payload.demand?.qualifiedLeads}</p>
                <Link to="/admin/analytics/demand">Ver demanda →</Link>
              </div>
              <div>
                <h4>SEO</h4>
                <p>Sesiones: {payload.seo?.seoSessions} · Leads: {payload.seo?.seoLeads}</p>
                <Link to="/admin/analytics/seo">Ver SEO →</Link>
              </div>
              <div>
                <h4>Precios</h4>
                <p>Cobertura fair price: {formatPercent(payload.pricing?.fairPriceCoveragePercent)}</p>
                <Link to="/admin/analytics/pricing">Ver precios →</Link>
              </div>
            </div>
          </section>
          <RecommendedActionsPanel actions={payload.recommendedActions} />
        </>
      ) : null}
    </MarketplaceAnalyticsShell>
  );
}
